import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  fetchMarkets, fetchSettings, fetchTexts, FALLBACK_SETTINGS, type Market, type SiteSettings,
} from "@/api/cms";
import { splitMarketPath, type MarketSlug } from "./markets";

/* The active market, its settings, and the one the visitor probably wants.
 *
 * SUGGESTION, NOT REDIRECTION. The design is explicit: a first visit from
 * another market shows a banner, and «never a redirect». A visitor who typed
 * /eg/ gets /eg/, whatever we think about where they are.
 *
 * HOW THE GUESS IS MADE: the browser's own IANA timezone, compared with
 * `countries.timezone`. No IP lookup, no geolocation prompt, no third party --
 * the standing law is zero Google anything, and a geo-IP service is one more
 * company learning where our visitors are for a banner. The timezone is
 * already on the device, costs no request, and is right often enough for a
 * dismissible suggestion. When it matches nothing, there is no suggestion.
 */

type MarketContextValue = {
  /** The market the page is rendering. Null only while the first fetch runs. */
  market: Market | null;
  /** Every market a visitor may be offered. A hidden one is not in here, so
   *  no switcher, footer list or suggestion can reach it. */
  markets: Market[];
  /** Every market including hidden ones. Admin screens need the whole list;
   *  nothing visitor-facing should use it. */
  allMarkets: Market[];
  /** True when this market exists but is not open to visitors yet. */
  comingSoon: boolean;
  /** True when the URL names a market the owner has hidden. */
  hiddenMarket: boolean;
  /** The URL prefix in force, or null on an unprefixed path. */
  slug: MarketSlug | null;
  settings: SiteSettings;
  /** Copy overrides for this market, by locale then key. Empty means "the
   *  designed wording", which is what the site shows until an admin edits it. */
  texts: Record<string, Record<string, string>>;
  loading: boolean;
  /** The market the browser's timezone suggests, when it is not the active one. */
  suggested: Market | null;
  dismissSuggestion: () => void;
};

const MarketContext = createContext<MarketContextValue | null>(null);

const DISMISS_KEY = "souqakar_market_suggestion_dismissed";

function browserTimezone(): string | null {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

export function MarketProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { slug } = splitMarketPath(pathname);

  const [allMarkets, setAllMarkets] = useState<Market[]>([]);
  const [settings, setSettings] = useState<SiteSettings>(FALLBACK_SETTINGS);
  const [texts, setTexts] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    const ctrl = new AbortController();
    let live = true;
    fetchMarkets(ctrl.signal)
      .then((rows) => { if (live) setAllMarkets(rows); })
      .catch(() => { /* the fallback inside fetchMarkets already ran; nothing left to try */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; ctrl.abort(); };
  }, []);

  /* KAR-132: a hidden market is not offered anywhere a visitor can see. */
  const markets = useMemo(
    () => allMarkets.filter((m) => m.visibility !== "hidden"),
    [allMarkets],
  );

  /* The market being rendered: the URL's slug when it names one, otherwise
   * the first OPEN market, which is what every URL meant before prefixes
   * existed.
   *
   * A SLUG WINS EVEN WHEN THAT MARKET IS NOT OPEN YET. Before this, the list
   * held only active markets, so /sa/ matched nothing, fell through to
   * markets[0] and served EGYPT'S listings under a Saudi URL -- prices in the
   * wrong currency for a country we have not opened, with nothing on the page
   * saying so. Now /sa/ resolves to Saudi Arabia and the screen says it is
   * coming; an empty market is a true thing to show, another country's
   * property is not. */
  const market = useMemo(() => {
    if (!allMarkets.length) return null;
    /* Resolved against ALL markets, hidden included: /sa/ has to resolve to
     * Saudi Arabia before anything can decide to send the visitor away from
     * it. Falling through to Egypt here would serve Egypt's listings under a
     * Saudi URL, which is the defect this whole area already had once. */
    return allMarkets.find((m) => m.slug === slug)
      ?? markets.find((m) => m.active)
      ?? markets[0]
      ?? allMarkets[0];
  }, [allMarkets, markets, slug]);

  /* True when the visitor is inside a market that has not opened. The pages
   * read this instead of each deciding for itself what an inactive market
   * looks like. */
  const comingSoon = !!market && market.visibility === "coming_soon";
  /* Only when the URL actually names it. A hidden market reached through the
   * default (unprefixed) route is not "the visitor asked for a hidden
   * market" -- it is a misconfiguration, and sending them away would leave
   * the site with nowhere to go. */
  const hiddenMarket = !!market && !!slug && market.slug === slug
    && market.visibility === "hidden";

  useEffect(() => {
    if (!market) return;
    const ctrl = new AbortController();
    let live = true;
    fetchSettings(market.slug, ctrl.signal).then((s) => { if (live) setSettings(s); });
    fetchTexts(market.slug, ctrl.signal).then((t) => { if (live) setTexts(t); });
    return () => { live = false; ctrl.abort(); };
  }, [market]);

  const suggested = useMemo(() => {
    if (dismissed || !market || markets.length < 2) return null;
    const tz = browserTimezone();
    if (!tz) return null;
    /* ONLY A MARKET THAT IS OPEN. Inactive markets joined this list so the
     * switcher could show them; without this guard a visitor in Riyadh
     * browsing /eg/ would be offered a door into a market with nothing behind
     * it. Offering to move someone somewhere empty is worse than saying
     * nothing. */
    /* Never suggest a market the owner has hidden, and never one that is not
     * open: both would offer a door with nothing behind it. */
    const guess = markets.find((m) =>
      m.active && m.visibility === "active" && m.timezone && m.timezone === tz);
    return guess && guess.id !== market.id ? guess : null;
  }, [dismissed, market, markets]);

  const dismissSuggestion = useCallback(() => {
    setDismissed(true);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* storage off */ }
  }, []);

  const value = useMemo<MarketContextValue>(
    () => ({ market, markets, allMarkets, comingSoon, hiddenMarket, slug, settings, texts, loading, suggested, dismissSuggestion }),
    [market, markets, allMarkets, comingSoon, hiddenMarket, slug, settings, texts, loading, suggested, dismissSuggestion],
  );
  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}

export function useMarket(): MarketContextValue {
  const v = useContext(MarketContext);
  if (!v) throw new Error("useMarket() outside MarketProvider");
  return v;
}
